// components/customers/Customer.jsx
import React, { useRef } from 'react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCustomers, createCustomer,
  getSingleCustomer, updateCustomer, deleteCustomer
} from '../../redux/features/sales/customerSlice';
import { useTable, usePagination, useGlobalFilter, useFilters, useColumnOrder  } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { MdEditSquare, MdDeleteForever } from 'react-icons/md';
import { AiOutlinePlus } from "react-icons/ai";
import ButtonLoading from '../../loader/ButtonLoader';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { Modal } from "bootstrap";
import { toast } from 'react-toastify';
import { ColumnToggle } from "./ColumnToggle";
import { EthiopianDateCell } from './EthiopianDateCell';

const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

const initialState = {
  name:        "",
  phone:       "",
  tinNumber:   "",
  address:     "",
  creditLimit: "",
};

export const Customer = () => {
  useRedirectLogOutUser();
  const dispatch = useDispatch();

  const addModalRef    = useRef(null);
  const editModalRef   = useRef(null);
  const deleteModalRef = useRef(null);

  const [form, setForm]         = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const { customers, customer } = useSelector((state) => state.customer);
  const { userType }            = useSelector((state) => state.auth);
  const isAdmin                 = userType === "admin";

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getCustomers());
  }, [dispatch]);

  // Populate edit form
  useEffect(() => {
    if (customer && editId) {
      setForm({
        name:        customer.name        ?? "",
        phone:       customer.phone       ?? "",
        tinNumber:   customer.tinNumber   ?? "",
        address:     customer.address     ?? "",
        creditLimit: customer.creditLimit ?? "",
      });
    }
  }, [customer, editId]);

  // ─── Modal openers ───────────────────────────────────────────
  const openAddModal = () => {
    setForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    dispatch(getSingleCustomer(id));
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ─── Add submit ───────────────────────────────────────────────
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Customer name is required");
    setIsLoading(true);
    await dispatch(createCustomer({
      name:        form.name,
      phone:       form.phone       || null,
      tinNumber:   form.tinNumber   || null,
      address:     form.address     || null,
      creditLimit: Number(form.creditLimit) || 0,
    }));
    setIsLoading(false);
    setForm(initialState);
    // keep open for adding more
    toast.success("Customer added — add another or close.");
  };

  // ─── Edit submit ──────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await dispatch(updateCustomer({
      id: editId,
      data: {
        name:        form.name,
        phone:       form.phone       || null,
        tinNumber:   form.tinNumber   || null,
        address:     form.address     || null,
        creditLimit: Number(form.creditLimit) || 0,
      }
    }));
    dispatch(getCustomers());
    Modal.getInstance(editModalRef.current).hide();
  };

  // ─── Delete ───────────────────────────────────────────────────
  const handleDelete = () => {
    dispatch(deleteCustomer(deleteId));
    Modal.getInstance(deleteModalRef.current).hide();
  };

  // ─── Shared form fields ───────────────────────────────────────
  const renderFormFields = () => (
    <div className="row g-3">
      <div className="col-12">
        <label className="h4">Customer Name <span className="text-danger">*</span></label>
        <input className="form-control form-control-lg border-2"
          type="text" name="name" value={form.name}
          onChange={handleChange} placeholder="Full name" />
      </div>
      <div className="col-6">
        <label className="h4">Phone</label>
        <input className="form-control form-control-lg border-2"
          type="text" name="phone" value={form.phone}
          onChange={handleChange} placeholder="Phone number" />
      </div>
      <div className="col-6">
        <label className="h4">TIN Number</label>
        <input className="form-control form-control-lg border-2"
          type="text" name="tinNumber" value={form.tinNumber}
          onChange={handleChange} placeholder="TIN (optional)" />
      </div>
      <div className="col-12">
        <label className="h4">Address</label>
        <input className="form-control form-control-lg border-2"
          type="text" name="address" value={form.address}
          onChange={handleChange} placeholder="Address (optional)" />
      </div>
      <div className="col-6">
        <label className="h4">Credit Limit (birr)</label>
        <input className="form-control form-control-lg border-2"
          type="number" min="0" name="creditLimit"
          value={form.creditLimit}
          onChange={handleChange} placeholder="0 = no credit" />
      </div>
    </div>
  );

  // ─── Table columns ────────────────────────────────────────────
  const columns = useMemo(() => {
    const base = [
      { Header: 'No', accessor: 'no', Cell: ({ row }) => row.index + 1 },
      { Header: 'Name',   accessor: 'name',  Filter: SelectColumnFilter, filter: 'multiSelectFilter', Cell: ({ value }) => dash(value) },
      { Header: 'Phone',  accessor: 'phone', Cell: ({ value }) => dash(value) },
      { Header: 'TIN',    accessor: 'tinNumber', Cell: ({ value }) => dash(value) },
      { Header: 'Address',accessor: 'address',   Cell: ({ value }) => dash(value) },
      {
        Header: 'Credit Limit',
        accessor: 'creditLimit',
        Cell: ({ value }) => value > 0 ? `${value.toLocaleString()} birr` : "—"
      },
      {
        Header: 'Total Debt',
        accessor: 'totalDebt',
        Cell: ({ value }) => value > 0
          ? <span className="text-danger fw-semibold">{value.toLocaleString()} birr</span>
          : <span className="text-success">—</span>
      },
      {
        Header: 'Date',
        accessor: 'createdAt',
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"

      },
    ];

    if (isAdmin) {
      base.push({
        Header: 'Action',
        Cell: ({ row }) => (
          <>
            <span className="text-warning" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openEditModal(row.original._id)}>
              <MdEditSquare size={24} />
            </span>
            <span className="text-danger" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openDeleteModal(row.original._id)}>
              <MdDeleteForever size={24} />
            </span>
          </>
        )
      });
    }

    return base;
  }, [isAdmin]);

  const data        = useMemo(() => Array.isArray(customers) ? customers : [], [customers]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, data, filterTypes,
    defaultColumn: { Filter: () => null }
  }, useFilters, useGlobalFilter, usePagination, useColumnOrder);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow, allColumns, getToggleHideAllColumnsProps
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>

      {/* ── ADD MODAL ─────────────────────────────────────── */}
      <div className="modal fade" ref={addModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <AiOutlinePlus color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Add Customer</h3>
                  <small className="text-muted fs-5">Modal stays open — add multiple customers.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <form onSubmit={handleAddSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">{renderFormFields()}</div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Close</button>
                {isLoading ? (
                  <ButtonLoading className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2" disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2">
                    Add Customer
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ────────────────────────────────────── */}
      <div className="modal fade" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Customer</h3>
                  <small className="text-muted fs-5">Update customer information.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <form onSubmit={handleEditSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">{renderFormFields()}</div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Cancel</button>
                <button type="submit"
                  className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2">
                  Update Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ──────────────────────────────────── */}
      <div className="modal fade" ref={deleteModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}>
                  <MdDeleteForever color="red" size={34} />
                </div>
                <div>
                  <h5 className="modal-title fw-semibold mb-0">Delete Customer</h5>
                  <small className="text-muted">This will deactivate the customer.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0">Are you sure you want to delete this customer?</p>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 gap-2">
              <button className="btn btn-primary border flex-fill rounded-3 fw-medium py-2 fs-2"
                data-bs-dismiss="modal">Cancel</button>
              <button className="btn btn-danger flex-fill rounded-3 fw-medium py-2 fs-2"
                onClick={handleDelete}>
                <MdDeleteForever size={18} className="me-1" />Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABLE ─────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <h4 className="mb-0 fs-2">Customers</h4>
        <button className="btn btn-primary rounded-3 fs-4" onClick={openAddModal}>
          <AiOutlinePlus className="me-1 fs-2" /> Add Customer
        </button>
      </div>

      <div className="px-4 mb-4">
        <div className="d-flex gap-3 align-items-center justify-content-between px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
            <ColumnToggle allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps} />

        </div>
      </div>



      <div className="px-4">
        <div className="table-responsive rounded-4 border mt-5" style={{ backgroundColor: "#ffffff" }}>
          <table className="table table-hover table-striped fs-4"
            {...getTableProps()}
            style={{ minWidth: "100%", fontSize: "12px", margin: 0 }}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th className="px-4" style={{ backgroundColor: "#faf8f5" }}
                      {...column.getHeaderProps()}>
                      {column.render('Header')}
                      {column.canFilter && column.render('Filter')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="px-4" style={{ backgroundColor: "#ffffff" }}
                        {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {page.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: '20px' }}>
            page <strong><em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}</strong>
          </span>
          <select className="form-select form-select-lg fs-4" style={{ maxWidth: '110px' }}
            value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50].map((s, i) => <option key={i} value={s}>Show {s}</option>)}
          </select>
          <li><button className="page-link fs-4" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button></li>
          <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
            <button className="page-link fs-4" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
            <button className="page-link fs-4" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li><button className="page-link fs-4" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button></li>
        </ul>
      </div>

    </div>
  );
};